import Drawer from "@via/components/drawer.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { type JSX, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RemoteServerForm } from "./remote-server-form.tsx";
import {
  useDeleteRemoteStorageCredential,
  useRemoteStorageCredentials,
} from "./remote-storage.api.tsx";
import {
  REMOTE_STORAGE_REFUSAL_MESSAGES,
  type RemoteStorageCredentialSummary,
} from "common";

/** The Remote server tile on Connect Data Source: pick, add, edit or remove a saved server, then connect (data-1-2). */
export default function RemoteStorageConnector(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, isLoading } = useRemoteStorageCredentials();
  const remove = useDeleteRemoteStorageCredential();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editing, setEditing] = useState<
    RemoteStorageCredentialSummary | null | "new"
  >(null);

  if (isLoading || !data) {
    return (
      <p className="py-6 text-center text-sm text-gray-500">
        Loading remote servers
      </p>
    );
  }
  if (!data.enabled) {
    return (
      <p className="mx-auto max-w-[850px] py-6 text-sm text-gray-700">
        {REMOTE_STORAGE_REFUSAL_MESSAGES.disabled}
      </p>
    );
  }

  const selected = data.credentials.find(c => c.id === selectedId) ?? null;
  const connect = () => {
    if (!selected) return;
    const params = new URLSearchParams({ credentialId: String(selected.id) });
    const datasetId = searchParams.get("datasetId");
    if (datasetId) params.set("datasetId", datasetId);
    navigate(
      `${ROUTES.DATA_SOURCE_PROVIDER_REMOTE_STORAGE}?${params.toString()}`
    );
  };

  return (
    <div className="mx-auto max-w-[850px] space-y-4 pb-5 pt-6">
      <div>
        <label
          className="block text-sm font-medium leading-6 text-gray-900"
          htmlFor="rs-select">
          Remote server
        </label>
        <select
          id="rs-select"
          className="mt-2 block w-full rounded-md border-0 py-1.5 px-2 ring-1 ring-inset ring-gray-300 sm:text-sm"
          value={selectedId ?? ""}
          onChange={e =>
            setSelectedId(e.target.value ? Number(e.target.value) : null)
          }>
          <option value="">Choose a remote server</option>
          {data.credentials.map(c => (
            <option key={c.id} value={c.id}>
              {`${c.name} (${String(c.variables.host)})`}
            </option>
          ))}
        </select>
        {selected && !selected.secretsReadable && (
          <p className="mt-1 text-xs text-rose-600">
            The saved password or key of this server cannot be read. Edit it and
            enter it again.
          </p>
        )}
      </div>
      <div className="flex flex-wrap justify-end gap-3">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300"
          onClick={() => setEditing("new")}>
          Add a remote server
        </button>
        {selected && (
          <>
            <button
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300"
              onClick={() => setEditing(selected)}>
              Edit
            </button>
            <button
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-rose-600 ring-1 ring-inset ring-gray-300"
              onClick={async () => {
                if (
                  window.confirm(
                    `Remove ${selected.name} and its saved password or key?`
                  )
                ) {
                  await remove.mutateAsync(selected.id);
                  setSelectedId(null);
                }
              }}>
              Remove
            </button>
          </>
        )}
        <button
          type="button"
          disabled={!selected}
          onClick={connect}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
          Connect
        </button>
      </div>
      <Drawer
        id="remote-server"
        open={null !== editing}
        setOpen={open => !open && setEditing(null)}>
        {null !== editing ? (
          <div className="p-4">
            <h2 className="mb-4 text-lg font-semibold">
              {"new" === editing
                ? "Add a remote server"
                : `Edit ${editing.name}`}
            </h2>
            <RemoteServerForm
              existing={"new" === editing ? undefined : editing}
              onSaved={saved => {
                setSelectedId(saved.id);
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
            />
          </div>
        ) : (
          <></>
        )}
      </Drawer>
    </div>
  );
}
